import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Search as SearchIcon, UserPlus, UserCheck, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Profile, Friend } from '../types/database';
import { cn } from '../lib/utils';
import { useAuth } from '../hooks/useAuth';

interface SearchProps {
  fullWidth?: boolean;
  onFriendRequestSent?: () => void;
}

interface SearchResult extends Profile {
  is_online?: boolean;
  friend_status?: 'pending' | 'accepted' | 'rejected' | null;
  is_sender?: boolean;
}

export default function Search({ fullWidth = false, onFriendRequestSent }: SearchProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [friendRequestLoading, setFriendRequestLoading] = useState<Record<string, boolean>>({});
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchUsers = async () => {
      if (!query.trim() || !user) {
        setResults([]);
        return;
      }

      try {
        setLoading(true);

        // Get friend relationships for the current user
        const { data: friendships, error: friendError } = await supabase
          .from('friends')
          .select('*')
          .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`);

        if (friendError) throw friendError;

        // Create a map of friend statuses
        const friendStatusMap = new Map<string, { status: string; isSender: boolean }>();
        friendships?.forEach(friendship => {
          const otherUserId = friendship.sender_id === user.id ? friendship.receiver_id : friendship.sender_id;
          friendStatusMap.set(otherUserId, {
            status: friendship.status,
            isSender: friendship.sender_id === user.id
          });
        });

        // Search for users by full name
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .ilike('full_name', `%${query}%`)
          .neq('id', user.id) // Exclude current user
          .order('full_name')
          .limit(10);

        if (error) throw error;

        // Process results to include friend status
        const processedResults = (data || [])
          .filter(profile => profile.id !== user.id) // Extra safety check to exclude current user
          .map(profile => {
            const friendStatus = friendStatusMap.get(profile.id);

            return {
              ...profile,
              friend_status: friendStatus?.status as 'pending' | 'accepted' | 'rejected' | null,
              is_sender: friendStatus?.isSender
            };
          });

        setResults(processedResults);
      } catch (error) {
        console.error('Error searching users:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(searchUsers, 300);
    return () => clearTimeout(timeoutId);
  }, [query, user]);

  const handleFriendRequest = async (profileId: string) => {
    if (!user) return;

    try {
      setFriendRequestLoading(prev => ({ ...prev, [profileId]: true }));

      // Check if there's an existing rejected friendship
      const { data: existingFriendship, error: checkError } = await supabase
        .from('friends')
        .select('*')
        .or(`and(sender_id.eq.${user.id},receiver_id.eq.${profileId}),and(sender_id.eq.${profileId},receiver_id.eq.${user.id})`)
        .eq('status', 'rejected');

      if (checkError) throw checkError;

      // If there's a rejected friendship, delete it first
      if (existingFriendship && existingFriendship.length > 0) {
        const { error: deleteError } = await supabase
          .from('friends')
          .delete()
          .eq('id', existingFriendship[0].id);

        if (deleteError) throw deleteError;
      }

      // Create new friend request
      const { error } = await supabase
        .from('friends')
        .insert({
          sender_id: user.id,
          receiver_id: profileId,
          status: 'pending'
        });

      if (error) throw error;

      // Update the local state
      setResults(prev =>
        prev.map(result =>
          result.id === profileId
            ? { ...result, friend_status: 'pending', is_sender: true }
            : result
        )
      );

      // Call the callback if provided
      onFriendRequestSent?.();
    } catch (error) {
      console.error('Error sending friend request:', error);
    } finally {
      setFriendRequestLoading(prev => ({ ...prev, [profileId]: false }));
    }
  };

  const handleUserClick = (profile: Profile) => {
    navigate(`/user/${profile.id}`);
    setIsOpen(false);
    setQuery('');
  };

  const getAvatarUrl = (profile: Profile) => {
    if (profile.avatar_url) {
      if (profile.avatar_url.startsWith('http')) {
        return profile.avatar_url;
      }
      const { data } = supabase.storage.from('avatars').getPublicUrl(profile.avatar_url);
      return data.publicUrl;
    }
    return `https://api.dicebear.com/7.x/initials/svg?seed=${profile.full_name}`;
  };

  const highlightMatch = (text: string) => {
    if (!query.trim() || !text) return text;

    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? (
        <span key={i} className="bg-yellow-100">{part}</span>
      ) : (
        part
      )
    );
  };

  const getFriendButton = (profile: SearchResult) => {
    if (friendRequestLoading[profile.id]) {
      return (
        <button
          disabled
          className="p-2 rounded-full bg-gray-100 text-gray-400"
        >
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
        </button>
      );
    }

    // Show Add Friend button if:
    // 1. No friendship exists, or
    // 2. Friendship was rejected (regardless of who rejected it)
    if (!profile.friend_status || profile.friend_status === 'rejected') {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleFriendRequest(profile.id);
          }}
          className="p-2 rounded-full hover:bg-gray-100 text-gray-600 hover:text-blue-500 transition-colors"
          title="Send friend request"
        >
          <UserPlus className="h-4 w-4" />
        </button>
      );
    }

    if (profile.friend_status === 'accepted') {
      return (
        <button
          disabled
          className="p-2 rounded-full bg-green-50 text-green-500"
          title="Friends"
        >
          <UserCheck className="h-4 w-4" />
        </button>
      );
    }

    if (profile.friend_status === 'pending') {
      return (
        <button
          disabled
          className="p-2 rounded-full bg-blue-50 text-blue-500"
          title={profile.is_sender ? "Friend request sent" : "Friend request received"}
        >
          <Clock className="h-4 w-4" />
        </button>
      );
    }

    return null;
  };

  return (
    <div ref={searchRef} className={cn("relative", fullWidth && "w-full")}>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search users..."
          className={cn(
            "pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
            fullWidth ? "w-full" : "w-64"
          )}
        />
        <SearchIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
      </div>

      {isOpen && (query.trim() || results.length > 0) && (
        <div className="absolute top-full mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50">
          {loading ? (
            <div className="p-4 text-center text-gray-500">
              Searching...
            </div>
          ) : results.length > 0 ? (
            <div className="max-h-96 overflow-y-auto">
              {results.map((profile) => (
                <div
                  key={profile.id}
                  onClick={() => handleUserClick(profile)}
                  className="flex items-center justify-between gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <img
                        src={getAvatarUrl(profile)}
                        alt={profile.full_name}
                        className="h-10 w-10 rounded-full object-cover bg-gray-100"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          img.src = `https://api.dicebear.com/7.x/initials/svg?seed=${profile.full_name}`;
                        }}
                      />
                      {profile.is_online && (
                        <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-white" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {highlightMatch(profile.full_name)}
                      </div>
                    </div>
                  </div>

                  {getFriendButton(profile)}
                </div>
              ))}
            </div>
          ) : query.trim() ? (
            <div className="p-4 text-center text-gray-500">
              No users found
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
